/**
 * Portfolio profiles service — the tiny per-student record that gives a student
 * a shareable PUBLIC portfolio identity, without ever exposing their full name.
 *
 * A profile document (id === ownerUid, one per student):
 *   {
 *     ownerUid,     // == doc id == users/{uid}
 *     firstName,    // FIRST NAME ONLY — the only name shown publicly
 *     shortCode,    // generated, unique; the public URL is /p/{shortCode}
 *                   //   e.g. "arjun-k3f9" (first-name slug + random suffix)
 *     headline,     // optional student-authored bio (sanitized)
 *     createdAt, updatedAt,
 *   }
 *
 * The profile holds no PII beyond a first name + a short bio the student writes,
 * so it is world-readable (that is the point of a public portfolio); the URL
 * deliberately never contains the full name. Only the owner may write it.
 */
import firestoreService, { where, limit, serverTimestamp } from './firestore.service';
import { LIMITS, SHORTCODE_ALPHABET } from 'features/portfolio/constants';
import { normalizeUsername, validateUsername } from 'features/portfolio/helpers';

const PORTFOLIO_PROFILES = 'portfolioProfiles';

// Uniqueness registry: one doc per taken username, keyed BY the handle
// (doc id === username). A `create` only fires when the doc is free, so a
// claim is atomic and race-proof — two students can never take the same one.
const USERNAME_REGISTRY = 'portfolioUsernames';

/** De-control, trim, length-cap a text field (bio) — by char code (no literal
 *  control chars in source); newlines collapse to spaces for a one-line bio. */
function cleanText(value = '', max = 240) {
  let out = '';
  for (const ch of String(value ?? '')) {
    const code = ch.charCodeAt(0);
    if (code === 10 || code === 13) out += ' ';
    else if (code >= 32 && code !== 127) out += ch;
  }
  return out.trim().slice(0, max);
}

/** Lowercase ASCII slug of a first name, e.g. "Arjun" → "arjun". */
function slugifyFirstName(name = '') {
  const slug = String(name)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 16);
  return slug || 'student';
}

/** N random chars from the non-ambiguous alphabet. */
function randomSuffix(n = 4) {
  let out = '';
  for (let i = 0; i < n; i += 1) {
    out += SHORTCODE_ALPHABET[Math.floor(Math.random() * SHORTCODE_ALPHABET.length)];
  }
  return out;
}

export const portfolioProfilesService = {
  /** Fetch a profile by its owner uid (doc id), or null. */
  getByOwner(ownerUid) {
    return firestoreService.getDocument(PORTFOLIO_PROFILES, ownerUid);
  },

  /**
   * Resolve a profile by its public short code (the /p/{shortCode} lookup).
   * Public read — used by the unauthenticated portfolio page. Returns the
   * record or null.
   */
  async getByShortCode(shortCode) {
    const code = String(shortCode || '').trim().toLowerCase();
    if (!code) return null;
    const matches = await firestoreService.getCollection(PORTFOLIO_PROFILES, [
      where('shortCode', '==', code),
      limit(1),
    ]);
    return matches[0] || null;
  },

  /** Realtime subscription to the owner's own profile (student portfolio page). */
  subscribeByOwner(ownerUid, onData, onError) {
    return firestoreService.subscribeDocument(PORTFOLIO_PROFILES, ownerUid, onData, onError);
  },

  /**
   * Return the student's profile, creating it on first use with a generated,
   * collision-checked short code. Idempotent: an existing profile (and its
   * shortCode) is never regenerated — only backfills a missing firstName.
   */
  async ensureProfile(ownerUid, firstName) {
    if (!ownerUid) return null;
    const existing = await this.getByOwner(ownerUid);
    if (existing) {
      // Backfill firstName if it was created empty (defensive; no-op normally).
      if (!existing.firstName && firstName) {
        await firestoreService.updateDocument(PORTFOLIO_PROFILES, ownerUid, {
          firstName: cleanText(firstName, 60),
        });
        return { ...existing, firstName: cleanText(firstName, 60) };
      }
      return existing;
    }

    const shortCode = await this.generateUniqueShortCode(firstName);
    // Register the auto-generated handle in the uniqueness registry so no one
    // can later claim it as a custom username. Best-effort: the code is already
    // collision-checked, and isUsernameAvailable still guards legacy gaps.
    await this.reserveHandle(ownerUid, shortCode).catch(() => {});

    const profile = {
      ownerUid,
      firstName: cleanText(firstName, 60) || 'Student',
      shortCode,
      headline: '',
    };
    // Keyed by ownerUid so there's exactly one profile per student.
    await firestoreService.setDocument(PORTFOLIO_PROFILES, ownerUid, {
      ...profile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return profile;
  },

  /**
   * Claim a handle in the uniqueness registry. The write is a `create` (the
   * registry has no update rule), so it succeeds only when the handle is free —
   * a taken handle rejects with a permission error. Returns the claimed handle.
   */
  async reserveHandle(ownerUid, handle) {
    const code = normalizeUsername(handle);
    await firestoreService.setDocument(USERNAME_REGISTRY, code, {
      ownerUid,
      createdAt: serverTimestamp(),
    });
    return code;
  },

  /**
   * Is `handle` free for `ownerUid`? Checks the atomic registry first, then
   * guards against legacy profiles (created before the registry) that may still
   * hold the same shortCode. Returns true when free OR already owned by this
   * student. Format is NOT validated here — call `validateUsername` for that.
   */
  async isUsernameAvailable(handle, ownerUid) {
    const code = normalizeUsername(handle);
    if (!code) return false;
    const claim = await firestoreService.getDocument(USERNAME_REGISTRY, code);
    if (claim) return claim.ownerUid === ownerUid;
    // No registry entry — make sure a pre-registry profile isn't using it.
    const existing = await this.getByShortCode(code);
    return !existing || existing.ownerUid === ownerUid;
  },

  /**
   * Set the student's chosen public username. First-come-first-served and
   * race-proof: the claim is a `create` in the registry, so a simultaneous
   * second claimant is rejected by the rules. On success the profile's
   * shortCode is repointed and the old handle released for reuse.
   *
   * Throws an Error with a student-friendly message on invalid/taken handles.
   * Returns the canonical (lowercased) handle that was set.
   */
  async setUsername(ownerUid, handle) {
    const code = normalizeUsername(handle);
    const formatError = validateUsername(code);
    if (formatError) throw new Error(formatError);

    const profile = await this.getByOwner(ownerUid);
    const current = profile?.shortCode || null;
    if (current === code) return code; // no change

    // Friendly pre-check (the create below is the real, atomic guard).
    const free = await this.isUsernameAvailable(code, ownerUid);
    if (!free) throw new Error('That username is already taken.');

    try {
      await this.reserveHandle(ownerUid, code);
    } catch {
      // Lost a race, or the handle was taken between the check and the claim.
      throw new Error('That username was just taken — please try another.');
    }

    await firestoreService.updateDocument(PORTFOLIO_PROFILES, ownerUid, {
      shortCode: code,
    });

    // Release the previous handle so it can be reused (best-effort).
    if (current && current !== code) {
      firestoreService.deleteDocument(USERNAME_REGISTRY, current).catch(() => {});
    }
    return code;
  },

  /** Generate a short code that isn't already taken (a few tries, then widen). */
  async generateUniqueShortCode(firstName) {
    const base = slugifyFirstName(firstName);
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const candidate = `${base}-${randomSuffix(attempt < 4 ? 4 : 6)}`;
      // eslint-disable-next-line no-await-in-loop
      const taken = await this.getByShortCode(candidate);
      if (!taken) return candidate;
    }
    // Extremely unlikely fallback — timestamp-based, effectively unique.
    return `${base}-${Date.now().toString(36).slice(-6)}`;
  },

  /** Update the student's editable bio/headline (sanitized). */
  updateBio(ownerUid, headline) {
    return firestoreService.updateDocument(PORTFOLIO_PROFILES, ownerUid, {
      headline: cleanText(headline, LIMITS.bio),
    });
  },
};

export default portfolioProfilesService;
